import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { APP_NAME, SLOGAN, LOGO, applyTheme } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Building2, UserRound, Loader2, ArrowLeft } from "lucide-react";
import { t, tf } from "@/lib/i18n";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [loginType, setLoginType] = useState("azienda"); // azienda | agente
  const [tab, setTab] = useState(params.get("mode") === "register" ? "register" : "login");
  const [busy, setBusy] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rName, setRName] = useState("");
  const [rEmail, setREmail] = useState("");
  const [rPassword, setRPassword] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [inviteCode, setInviteCode] = useState("");

  const isCompany = loginType === "azienda";

  useEffect(() => {
    applyTheme(isCompany ? "blue" : "green");
  }, [isCompany]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      navigate(u.role === "admin" ? "/admin" : u.needs_onboarding ? "/onboarding" : "/dashboard");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Accesso non riuscito");
    } finally {
      setBusy(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await register({
        name: rName,
        email: rEmail,
        password: rPassword,
        role: isCompany ? "company" : "agent",
        company_name: isCompany ? companyName : undefined,
        invite_code: !isCompany ? inviteCode : undefined,
      });
      if (u.needs_verification) {
        toast.success(t("Ti abbiamo inviato un'email di conferma. Controlla la posta per attivare l'account."));
        setTab("login");
      } else {
        navigate(u.needs_onboarding ? "/onboarding" : "/dashboard");
      }
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Registrazione non riuscita");
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-background grid lg:grid-cols-2">
      {/* Brand side */}
      <div className={`hidden lg:flex flex-col justify-between p-12 border-r border-border relative overflow-hidden ${isCompany ? "bg-blue-50" : "bg-emerald-50"}`}>
        <div aria-hidden className="absolute inset-0 pointer-events-none">
          <span className="login-blob login-blob-a bg-primary/20" />
          <span className="login-blob login-blob-b bg-sky-300/30" />
          <span className="login-blob login-blob-c bg-emerald-300/25" />
          <div className="absolute inset-0 bg-[radial-gradient(rgba(15,23,42,0.07)_1px,transparent_1px)] [background-size:22px_22px]" />
        </div>
        <button onClick={() => navigate("/")} className="flex items-center gap-2.5 w-fit relative">
          <img src={LOGO} alt={APP_NAME} className="h-10 w-10 rounded-lg" />
          <span className="font-display text-xl font-extrabold tracking-tight">{APP_NAME}</span>
        </button>
        <motion.div key={isCompany ? "c" : "a"} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }} className="space-y-5 max-w-md relative">
          <h1 className="font-display text-4xl xl:text-5xl font-extrabold leading-[1.05] tracking-tight">
            {isCompany ? (
              <>{t("Gestisci la tua")}<br /><span className="text-primary">{t("rete vendita.")}</span></>
            ) : (
              <>{t("Prendi ordini,")}<br /><span className="text-primary">{t("ovunque tu sia.")}</span></>
            )}
          </h1>
          <p className="text-muted-foreground text-base leading-relaxed">
            {isCompany
              ? t("Catalogo, agenti, ordini e statistiche in un'unica piattaforma. Inizia con 14 giorni gratis.")
              : t("Accedi con le credenziali fornite dalla tua azienda e inizia a raccogliere ordini in pochi tap.")}
          </p>
        </motion.div>
        <p className="text-xs text-muted-foreground relative">{SLOGAN}</p>
      </div>

      {/* Form side */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-md">
          <button onClick={() => navigate("/")} className="lg:hidden flex items-center gap-2 text-sm text-muted-foreground mb-6">
            <ArrowLeft className="h-4 w-4" /> {APP_NAME}
          </button>

          <div className="flex justify-end mb-4"><LanguageSwitcher /></div>
          {/* Login type switch */}
          <div className="grid grid-cols-2 gap-2 p-1 bg-secondary rounded-xl mb-6">
            <button
              type="button"
              data-testid="login-type-azienda-button"
              onClick={() => setLoginType("azienda")}
              className={`flex items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold transition-all ${
                isCompany ? "bg-white shadow-sm text-blue-700" : "text-muted-foreground"
              }`}
            >
              <Building2 className="h-4 w-4" /> {t("Azienda")}
            </button>
            <button
              type="button"
              data-testid="login-type-agente-button"
              onClick={() => setLoginType("agente")}
              className={`flex items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold transition-all ${
                !isCompany ? "bg-white shadow-sm text-emerald-700" : "text-muted-foreground"
              }`}
            >
              <UserRound className="h-4 w-4" /> {t("Agente")}
            </button>
          </div>

          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="grid grid-cols-2 w-full mb-6">
              <TabsTrigger value="login" data-testid="auth-tab-login">{t("Accedi")}</TabsTrigger>
              <TabsTrigger value="register" data-testid="auth-tab-register">
                {isCompany ? t("Registrati") : t("Iscriviti")}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label>{isCompany ? t("Email o nome utente") : t("Email")}</Label>
                  <Input data-testid="login-email-input" type={isCompany ? "text" : "email"} autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("nome@email.it")} />
                </div>
                <div className="space-y-2">
                  <Label>{t("Password")}</Label>
                  <Input data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
                </div>
                <Button data-testid="login-submit-btn" type="submit" disabled={busy} className="w-full h-11 font-semibold">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (isCompany ? t("Accedi come azienda") : t("Accedi come agente"))}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="space-y-2">
                  <Label>{t("Nome e cognome")}</Label>
                  <Input data-testid="register-name-input" required value={rName} onChange={(e) => setRName(e.target.value)} placeholder={t("Mario Rossi")} />
                </div>
                {isCompany ? (
                  <div className="space-y-2">
                    <Label>{t("Nome azienda")}</Label>
                    <Input data-testid="register-company-input" required value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder={t("Acme S.r.l.")} />
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label>{t("Codice invito azienda")}</Label>
                    <Input data-testid="register-invite-input" required value={inviteCode} onChange={(e) => setInviteCode(e.target.value)} placeholder={t("ACME-1A2B3C")} className="font-mono-j uppercase" />
                  </div>
                )}
                <div className="space-y-2">
                  <Label>{t("Email")}</Label>
                  <Input data-testid="register-email-input" type="email" required value={rEmail} onChange={(e) => setREmail(e.target.value)} placeholder={t("nome@email.it")} />
                </div>
                <div className="space-y-2">
                  <Label>{t("Password")}</Label>
                  <Input data-testid="register-password-input" type="password" required minLength={6} value={rPassword} onChange={(e) => setRPassword(e.target.value)} placeholder={t("min. 6 caratteri")} />
                </div>
                <Button data-testid="register-submit-btn" type="submit" disabled={busy} className="w-full h-11 font-semibold">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : isCompany ? t("Inizia 14 giorni gratis") : t("Crea account agente")}
                </Button>
                {isCompany && <p className="text-xs text-muted-foreground text-center">{t("Nessuna carta richiesta per la prova.")}</p>}
              </form>
            </TabsContent>
          </Tabs>

          <div className="flex items-center gap-3 my-6">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs text-muted-foreground uppercase tracking-wider">{t("oppure")}</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <Button data-testid="google-login-btn" variant="outline" onClick={googleLogin} className="w-full h-11 gap-3">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5" />
            {t("Continua con Google")}
          </Button>
        </motion.div>
      </div>
    </div>
  );
}
